import { useTracking } from 'react-components/tracking';

/**
 * Hook for generating form field tracking props
 * @param trackingName - The name of the tracking context for this field
 * @returns Object containing tracking props for form fields
 */
export const useFormTracking = (trackingName?: string) => {
  const { generateTrackingProps } = useTracking();

  if (!trackingName) {
    return {};
  }

  return generateTrackingProps(trackingName);
};

/**
 * Hook for generating option tracking props in form fields
 * @param trackingName - The name of the tracking context for this field
 * @param sensitive - Whether the options should be tracked for privacy
 * @param options - The options array to generate tracking for
 * @returns Function that generates tracking props for individual options
 */
export const useFormOptionTracking = (
  trackingName?: string,
  sensitive: boolean = true,
  options: unknown[] = []
) => {
  const { generateTrackingProps } = useTracking();

  const generateOptionTracking = (value: unknown, _label: string) => {
    if (!trackingName) {
      return {};
    }

    if (sensitive) {
      // For sensitive data, use index-based tracking for privacy
      const optionIndex = options.findIndex(option =>
        typeof option === 'object' && option !== null && 'value' in option
          ? option.value === value
          : option === value
      );

      if (optionIndex === -1) {
        return {};
      }

      return generateTrackingProps(`${trackingName}-option-${optionIndex}`);
    }

    // For non-sensitive data, use the actual value
    return generateTrackingProps(`${trackingName}-option-${value}`);
  };

  return { generateOptionTracking };
};
